import React, {useCallback, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ShortGuest} from '@chekin/core';
import {ConfirmationDialog, useModalControls, useTimeoutRef} from '@chekinapp/ui';
import {useErrorModal} from '../../../hooks/useErrorModal';
import {useIVStore} from '../../../store/useIVStore';
import {getGuestRowState} from './guestRoster';
import {DeleteGuestStatusModal} from './DeleteGuestStatusModal';
import {useDeleteGuest} from './useDeleteGuest';

const DELETE_GUEST_MODAL_TIMEOUT = 3000;

export type GuestRowActions = {
  getEditPath?: (guest: ShortGuest, isLeader?: boolean) => string | undefined;
  canRemoveRegistered?: boolean;
  onGuestDeleted?: (guestId: string) => void;
};

type Params = GuestRowActions & {
  guest: ShortGuest;
  isLeader?: boolean;
};

export function useGuestRowActions({
  guest,
  isLeader,
  getEditPath,
  canRemoveRegistered,
  onGuestDeleted,
}: Params) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {resetAllIVState} = useIVStore();
  const {ErrorModal, displayError} = useErrorModal();
  const closeModalTimeoutRef = useTimeoutRef();
  const {isOpen, openModal, closeModal} = useModalControls();
  const {
    isOpen: isConfirmOpen,
    openModal: openConfirm,
    closeModal: closeConfirm,
  } = useModalControls();
  const {mutate: deleteGuestMutate, status: deleteGuestStatus} = useDeleteGuest({
    onDeleted: onGuestDeleted,
    onError: displayError,
    onMutate: openModal,
    onSuccess: () => {
      closeModalTimeoutRef.current = setTimeout(() => {
        closeModal();
      }, DELETE_GUEST_MODAL_TIMEOUT);
    },
  });

  const state = getGuestRowState(guest);
  const isEditable = state !== 'registered';
  const canRemove = (isEditable || canRemoveRegistered) && !!guest.id;
  const guestName = guest.full_name || t('unknown_guest');

  const goToEdit = useCallback(() => {
    const link = getEditPath?.(guest, isLeader);

    if (isEditable && link) {
      resetAllIVState();
      navigate(link);
    }
  }, [getEditPath, guest, isLeader, isEditable, navigate, resetAllIVState]);

  const handleConfirmDelete = useCallback(() => {
    closeConfirm();
    if (canRemove) {
      deleteGuestMutate(guest.id);
    }
  }, [closeConfirm, canRemove, deleteGuestMutate, guest.id]);

  const dropdownOptions = useMemo(
    () => [
      {
        id: 2,
        label: t('edit_guest'),
        onClick: goToEdit,
        hidden: !isEditable,
      },
      {
        id: 3,
        label: t('remove_from_booking'),
        onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          event.preventDefault();
          openConfirm();
        },
        hidden: !canRemove,
      },
    ],
    [goToEdit, isEditable, canRemove, openConfirm, t],
  );

  const showActions = !!guest.id && dropdownOptions.some(action => !action.hidden);

  const dialogs = (
    <>
      <ConfirmationDialog
        open={isConfirmOpen}
        onOpenChange={open => (open ? openConfirm() : closeConfirm())}
        title={t('remove_guest_title', {name: guestName})}
        description={t('remove_guest_description')}
        confirmText={t('remove_from_booking')}
        cancelText={t('cancel')}
        onConfirm={handleConfirmDelete}
      />
      <ErrorModal />
      <DeleteGuestStatusModal open={isOpen} state={deleteGuestStatus} />
    </>
  );

  return {state, isEditable, goToEdit, dropdownOptions, showActions, dialogs};
}
